import { useRef, useState } from 'react';

import PinEntryForm from '../components/PinEntryForm';

import { Button } from '@/components';
import useAuth from '@/hooks/useAuth';
import useToast from '@/hooks/useToast';
import {
  GoldDateRegistration,
  popupInscriptionData,
} from '@/services/goldDate';
import { ResponseInfoFeoroUsuario, TAPorPremioTO } from '@/types/goldDate';
import { tourneyModalState } from '@/types/tda';
import { formatNumberToLocale } from '@/utils';

const defaultValues = {
  title: '',
  open: false,
  content: null,
  openSuccessModal: false,
};

export const useGoldDateParticipation = ({
  dataInitUsuarioOro,
}: {
  dataInitUsuarioOro?: ResponseInfoFeoroUsuario;
}) => {
  const { userInitialData } = useAuth();
  const toast = useToast();
  const [tourneyModal, setTourneyModal] =
    useState<tourneyModalState>(defaultValues);
  const [successModal, setSuccessModal] =
    useState<tourneyModalState>(defaultValues);
  const ref = useRef<() => void>();

  const handleTourneyModal = (updates: Partial<tourneyModalState>) => {
    setTourneyModal((prev) => ({ ...prev, ...updates }));
  };

  const handleSuccessModal = (updates: Partial<tourneyModalState>) => {
    setSuccessModal((prev) => ({ ...prev, ...updates }));
  };

  const successContent = () => (
    <div className="flex flex-col items-center content-center mt-4">
      <h1 className="font-bold uppercase text-center">
        ¡Ya estás participando!
      </h1>
      {/* <p className="text-center font-semibold">{`Hacé tus cambios antes del inicio de la competencia en la ${item.nombreFecha} del torneo.`}</p> */}
      <span className="font-light text-center mt-4">
        Recordá que podés anotarte en tantas Fechas de Oro como quieras
      </span>
    </div>
  );

  const freeEntryContent = (
    item: TAPorPremioTO,
    pinSugerido: string | undefined
  ): JSX.Element => {
    const handleAccept = async () => {
      const res = await GoldDateRegistration();

      if (res.ok) {
        handleTourneyModal({
          open: false,
          title: '',
          content: null,
          openSuccessModal: true,
        });
        handleSuccessModal({
          title: item.nombre,
          content: successContent(),
        });
      } else {
        if (res.codError === 3303) {
          toast.show(
            `No existe cupo para la Fecha de Oro seleccionada, en unos minutos podrás participar en otra Fecha de Oro por ${formatNumberToLocale(
              item.premio ?? 0
            )}`
          );
          handleTourneyModal({
            open: false,
          });
        } else {
          if (res.codError === 3507) {
            toast.show(res.mensaje);
            handleTourneyModal({
              open: false,
            });
          } else {
            toast.show(
              res.codError === 3300
                ? 'La inscripción libre ya fue utilizada en este torneo.'
                : res.mensaje
            );
            handleSuccessModal({
              title: item.nombre,
              content: successContent(),
            });
            handleTourneyModal({
              content: (
                <PinEntryForm
                  handleSuccessModal={handleSuccessModal}
                  handleTourneyModal={handleTourneyModal}
                  item={{ ...item, nombre: item.nombre ?? '' }}
                  pinSugerido={pinSugerido}
                  pinValue={formatNumberToLocale(
                    dataInitUsuarioOro?.precioDeListaPin ?? 0
                  )}
                  successContent={successContent}
                />
              ),
            });
          }
        }
      }
    };

    return (
      <div className="flex flex-col items-center content-center mt-4">
        <h1 className="font-bold text-center">
          Por ser el primer participante de este torneo, no necesitás ingresar
          un PIN Especial.
        </h1>
        <h1 className="font-bold text-center">Tu inscripción es libre.</h1>
        <span className=" text-center">
          El ganador del Torneo se lleva{' '}
          {`$${formatNumberToLocale(dataInitUsuarioOro?.premio ?? 0)}`}
        </span>
        <div className="flex space-x-4 mt-4">
          <Button
            className="w-[8rem]"
            type="button"
            variant="secondary"
            onClick={() =>
              handleTourneyModal({
                open: false,
                title: '',
                content: null,
              })
            }
          >
            Cancelar
          </Button>
          <Button
            className="w-[8rem]"
            type="button"
            variant="primary"
            onClick={handleAccept}
          >
            Aceptar
          </Button>
        </div>
      </div>
    );
  };

  // cuando el  usuario toca el boton participar
  // se verifica si el pin gratuito fue utilizado
  // si fue utilizado abre el modal para ingresar pin
  // si no fue utilizado se abre el modal de inscripcion libre
  const onCardButtonClick = async () => {
    if (userInitialData?.dtExtranjero) {
      toast.show(
        "La participación en esta competencia es exclusiva para DT's argentinos"
      );
      return;
    }
    const inscriptionData = await popupInscriptionData();
    if (inscriptionData.pinObligatorio && inscriptionData.ta) {
      handleTourneyModal({
        content: (
          <PinEntryForm
            handleSuccessModal={handleSuccessModal}
            handleTourneyModal={handleTourneyModal}
            item={{
              ...(inscriptionData.ta as TAPorPremioTO),
              nombre: (inscriptionData.ta as TAPorPremioTO).nombre ?? '',
            }}
            pinSugerido={inscriptionData.pinSugerido}
            pinValue={formatNumberToLocale(
              dataInitUsuarioOro?.precioDeListaPin ?? 0
            )}
            successContent={successContent}
          />
        ),
        open: true,
        title: `Participá en ${inscriptionData.ta?.nombre}`,
      });
    } else {
      handleTourneyModal({
        content: freeEntryContent(
          inscriptionData.ta as TAPorPremioTO,
          inscriptionData.pinSugerido
        ),
        open: true,
        title: `Participá de la ${inscriptionData.ta?.nombre}`,
      });
    }
  };

  return {
    tourneyModal,
    successModal,
    handleTourneyModal,
    handleSuccessModal,
    onCardButtonClick,
    ref,
  };
};
